import { useQuery } from '@apollo/client';
import { ALL_MODULES, FormTemplate, Loader } from '@nile/components';
import {
  GET_ENTITY_PROPERTIES,
  AccountClient,
  SystrayAccountClient,
  SystrayClient
} from '@nile/graphql';
import { cloneDeep, filter, isEmpty, map, pick } from 'lodash';
import React, { useState, useEffect } from 'react';
import { GET_ALL_FIELDS_V2 } from '@nile/banyan/src/Graphql/Mutations/ticket';
import { useFlags } from 'launchdarkly-react-client-sdk';

function CustomFormTemplate(props) {
  const {
    selectedFormTemplate,
    updateField,
    ticket,
    disableSelection,
    contentHeight,
    mandatoryFieldsOnCreation,
    setMandatoryFieldsOnCreation,
    errors,
    systray = false,
    dependantFieldEnabled = false,
    headerContext = {}
  } = props;
  const flags = useFlags();
  const [customData, setCustomData] = useState([]);
  const [defaultData, setDefaultData] = useState([]);
  const [dependantFields, setDependantFields] = useState([]);

  const [customForm, setCustomForm] = useState({
    fieldDetails: JSON.parse(selectedFormTemplate?.fieldDetails || null) || {}
  });

  const { loading } = useQuery(GET_ALL_FIELDS_V2, {
    fetchPolicy: 'network-only',
    client: systray ? SystrayAccountClient : AccountClient,
    variables: { input: ALL_MODULES.ticket },
    context: headerContext,
    onCompleted({ getAllFields }) {
      const mandatoryFields = getAllFields.filter(
        (el) => el?.mandatoryOnCreate
      );
      let customFields = [];
      let defaultFields = [];
      getAllFields.forEach((el) => {
        const formOptions = el?.options || el?.fieldOptions;
        const options = formOptions && formOptions.map((opt) => opt.value);
        const fieldData = {
          ...el,
          options: options || null
        };

        if (el?.fieldCategory === 'CUSTOM') {
          customFields = [...customFields, fieldData];
        }

        if (el?.fieldCategory === 'DEFAULT') {
          const defaultFieldData = {
            ...fieldData,
            name: fieldData?.columnName,
            attributePlaceHolder: {
              key: fieldData?.label,
              label: fieldData?.label
            },
            nestedTo: fieldData?.parentField?.columnName,
            dataType: 'string'
          };
          defaultFields = [...defaultFields, defaultFieldData];
        }
      });

      setCustomData(customFields);
      setDefaultData(defaultFields);

      if (setMandatoryFieldsOnCreation) {
        setMandatoryFieldsOnCreation(mandatoryFields);
      }
    }
  });

  useEffect(() => {
    setCustomForm({
      fieldDetails: JSON.parse(selectedFormTemplate?.fieldDetails || null)
    });
  }, [selectedFormTemplate?.formId, selectedFormTemplate?.fieldDetails]);

  const dependantFieldsMap =
    flags?.dependentField || (systray && dependantFieldEnabled)
      ? []
      : [
          { name: 'subcategory', disableEdit: 'true' },
          { name: 'subcause', disableEdit: 'true' }
        ];
  const {
    loading: entitiesLoading,
    error: entitiesError,
    data: allEntityData
  } = useQuery(GET_ENTITY_PROPERTIES, {
    client: systray ? SystrayClient : AccountClient,
    context: headerContext,
    onCompleted: (newData) => {
      const dependantData = [];
      const Ticket = newData.getEntitiesMeta
        .find((el) => el.name === 'Ticket')
        .attributes.filter((item) => {
          return dependantFieldsMap.some((el) => el.name === item.name);
        });
      Ticket.forEach((el) => {
        const currentField = dependantFieldsMap.filter(
          (e) => e.name === el.name
        )[0];
        if (el.dataType !== 'entity') {
          dependantData.push({
            ...el,
            type: 'default',
            disableEdit: currentField?.disableEdit || false
          });
        }
      });
      setDependantFields(dependantData);
    }
  });

  const updateDepedentFormField = (field, value) => {
    let updatedPreview = cloneDeep(customForm.fieldDetails.layout);
    const ticketMeta = allEntityData?.getEntitiesMeta?.find(
      (el) => el?.name === 'Ticket'
    );
    const currentFld = ticketMeta?.attributes?.find(
      (attr) => attr?.name === field
    );
    const nestedChild = [];
    if (currentFld?.nestedAttributes) {
      const getNestedChilds = (fld) => {
        fld?.nestedAttributes?.map((el) => {
          const child = ticketMeta?.attributes?.find(
            (attr) => attr?.name === el
          );
          nestedChild.push(child);
          if (child?.nestedAttributes) {
            getNestedChilds(child);
          }
        });
      };
      getNestedChilds(currentFld);
    }

    const resetFieldValue = (fld, vlu) => {
      const elementIndex = updatedPreview.findIndex(
        (el) =>
          el.field?.fieldProps?.name === fld ||
          (el.field?.fieldProps?.name &&
            fld?.name &&
            el.field?.fieldProps?.name === fld?.name)
      );
      if (elementIndex !== -1) {
        updatedPreview[elementIndex] = {
          ...updatedPreview[elementIndex],
          field: {
            ...updatedPreview[elementIndex].field,
            fieldProps: {
              ...updatedPreview[elementIndex].field.fieldProps,
              selectedValue: vlu?.label || vlu
            }
          }
        };
      }
    };

    resetFieldValue(field, value);
    if (!isEmpty(nestedChild)) {
      nestedChild?.map((el) => resetFieldValue(el, null));
    }

    setCustomForm({
      ...customForm,
      fieldDetails: {
        layout: updatedPreview
      }
    });
    props.updateSelectedFormTemplate({
      ...props.selectedFormTemplate,
      fieldDetails: JSON.stringify({
        layout: updatedPreview
      })
    });

    let customFieldProperties = ticket?.customFields;
    const resetTicketFields = (fld, vlu) => {
      if (fld?.isCustom) {
        customFieldProperties = customFieldProperties
          ? { ...customFieldProperties, [fld?.name]: vlu }
          : { [fld?.name]: vlu };
      }
    };

    resetTicketFields(currentFld, value);
    if (!isEmpty(nestedChild)) {
      nestedChild?.map((el) => resetTicketFields(el, null));
    }
    const customFieldNames = map(
      filter([...nestedChild, currentFld], { isCustom: true }),
      'name'
    );
    const customFieldsObject = pick(customFieldProperties, customFieldNames);
    updateField(
      'customFields',
      { ...customFieldProperties, ...customFieldsObject },
      mandatoryFieldsOnCreation,
      customForm
    );

    if (!isEmpty(nestedChild)) {
      nestedChild?.map((el) => resetTicketFields(el, null));
    }
  };

  const updateFormField = (field, value) => {
    const updatedPreview = cloneDeep(customForm.fieldDetails.layout);
    const dependantField = updatedPreview.find(
      (el) => el?.field?.fieldProps?.nestedTo === field
    );
    const elementIndex = updatedPreview.findIndex(
      (el) => el.field?.fieldProps?.name === field
    );
    if (flags?.dependentField || (systray && dependantFieldEnabled)) {
      updateDepedentFormField(field, value);
    } else {
      updatedPreview[elementIndex] = {
        ...updatedPreview[elementIndex],
        field: {
          ...updatedPreview[elementIndex].field,
          fieldProps: {
            ...updatedPreview[elementIndex].field.fieldProps,
            selectedValue: value?.label || value // check for custom field
          }
        }
      };

      if (dependantField) {
        const dependantIndex = updatedPreview.findIndex(
          (el) =>
            el.field?.fieldProps?.name ===
            dependantField?.field?.fieldProps?.name
        );
        updatedPreview[dependantIndex] = {
          ...updatedPreview[dependantIndex],
          field: {
            ...updatedPreview[dependantIndex].field,
            fieldProps: {
              ...updatedPreview[dependantIndex].field.fieldProps,
              selectedValue: null // check for custom field
            }
          }
        };
      }

      setCustomForm({
        ...customForm,
        fieldDetails: {
          layout: updatedPreview
        }
      });
      props.updateSelectedFormTemplate({
        ...props.selectedFormTemplate,
        fieldDetails: JSON.stringify({
          layout: updatedPreview
        })
      });
      if (updatedPreview[elementIndex]?.field?.type === 'custom') {
        const customFieldProperties = ticket?.customFields
          ? { ...ticket.customFields, [field]: value }
          : { [field]: value };
        updateField(
          'customFields',
          customFieldProperties,
          mandatoryFieldsOnCreation,
          customForm
        );
      }
    }
    if (updatedPreview[elementIndex]?.field?.type !== 'custom') {
      updateField(
        field,
        value.label || value,
        mandatoryFieldsOnCreation,
        customForm
      );
    }
  };
  const builderActions = {
    updateFormField: updateFormField
  };

  if (entitiesLoading || entitiesError || !defaultData.length || loading)
    return (
      <div className="flex-center full-height">
        <Loader />
      </div>
    );
  return (
    <FormTemplate
      context={{ value: customForm, ticket: ticket }}
      disableSelection={disableSelection}
      contentHeight={contentHeight}
      passedConditions={[]}
      builderActions={builderActions}
      defaultData={[...defaultData, ...dependantFields]}
      customFieldData={customData}
      mandatoryFieldsOnCreation={mandatoryFieldsOnCreation}
      errors={errors}
      systray={systray}
      dependantFieldEnabled={dependantFieldEnabled}
    />
  );
}

export default CustomFormTemplate;
